import { useState, useCallback } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';

const ONBOARDING_STEPS = [
  'welcome',
  'gtd-overview',
  'brain-dump',
  'statuses',
  'processing-demo',
  'calendar',
  'daily-rhythm',
  'completion'
];

export function useOnboarding() {
  const { user, userProfile, clearNewUserFlag } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);

  // Get onboarding state from user profile
  const onboarding = userProfile?.onboarding || {
    completedAt: null,
    skippedAt: null,
    currentStep: 'welcome',
    completedSteps: [],
    brainDumpCount: 0,
    calendarConnected: false,
    firstDailyReviewAt: null,
    firstWeeklyReviewAt: null
  };

  const needsOnboarding = !onboarding.completedAt && !onboarding.skippedAt;
  const currentStepIndex = ONBOARDING_STEPS.indexOf(onboarding.currentStep || 'welcome');

  // Update onboarding state in Firestore
  const updateOnboardingState = useCallback(async (updates) => {
    if (!user?.uid) return;

    setIsUpdating(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        'onboarding': {
          ...onboarding,
          ...updates
        },
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating onboarding state:', error);
    } finally {
      setIsUpdating(false);
    }
  }, [user?.uid, onboarding]);

  // Go to next step
  const goToNextStep = useCallback(async () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < ONBOARDING_STEPS.length) {
      const nextStep = ONBOARDING_STEPS[nextIndex];
      const completedSteps = [...new Set([...onboarding.completedSteps, onboarding.currentStep])];
      await updateOnboardingState({
        currentStep: nextStep,
        completedSteps
      });
    }
  }, [currentStepIndex, onboarding, updateOnboardingState]);

  // Go to previous step
  const goToPreviousStep = useCallback(async () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      const prevStep = ONBOARDING_STEPS[prevIndex];
      await updateOnboardingState({
        currentStep: prevStep
      });
    }
  }, [currentStepIndex, updateOnboardingState]);

  // Go to specific step
  const goToStep = useCallback(async (step) => {
    if (ONBOARDING_STEPS.includes(step)) {
      await updateOnboardingState({
        currentStep: step
      });
    }
  }, [updateOnboardingState]);

  // Complete onboarding
  const completeOnboarding = useCallback(async () => {
    await updateOnboardingState({
      completedAt: new Date(),
      currentStep: 'completion',
      completedSteps: ONBOARDING_STEPS
    });
    clearNewUserFlag();
  }, [updateOnboardingState, clearNewUserFlag]);

  // Skip onboarding
  const skipOnboarding = useCallback(async () => {
    await updateOnboardingState({
      skippedAt: new Date()
    });
    clearNewUserFlag();
  }, [updateOnboardingState, clearNewUserFlag]);

  // Update brain dump count
  const incrementBrainDumpCount = useCallback(async () => {
    await updateOnboardingState({
      brainDumpCount: (onboarding.brainDumpCount || 0) + 1
    });
  }, [onboarding.brainDumpCount, updateOnboardingState]);

  // Mark calendar as connected
  const markCalendarConnected = useCallback(async () => {
    await updateOnboardingState({
      calendarConnected: true
    });
  }, [updateOnboardingState]);

  return {
    onboarding,
    needsOnboarding,
    currentStep: onboarding.currentStep || 'welcome',
    currentStepIndex,
    totalSteps: ONBOARDING_STEPS.length,
    isUpdating,
    steps: ONBOARDING_STEPS,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    completeOnboarding,
    skipOnboarding,
    incrementBrainDumpCount,
    markCalendarConnected,
    updateOnboardingState
  };
}

export { ONBOARDING_STEPS };
